<template>
  <div>
    <!-- Dashboard metrics - Using standard B-Card components -->
    <b-row
      class="match-height mb-2"
      aria-label="Statistiques des poids volumétriques"
    >
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-primary">
          <div class="mb-1">
            <feather-icon
              icon="PackageIcon"
              size="21"
              class="text-primary"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalWeights || 0 }}
          </h2>
          <p class="card-text">
            Total des poids volumétriques
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-success">
          <div class="mb-1">
            <feather-icon
              icon="CheckCircleIcon"
              size="21"
              class="text-success"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ activeCount }}
          </h2>
          <p class="card-text">
            Poids actifs
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-warning">
          <div class="mb-1">
            <feather-icon
              icon="TruckIcon"
              size="21"
              class="text-warning"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ minWeight }}
          </h2>
          <p class="card-text">
            Poids minimum
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-info">
          <div class="mb-1">
            <feather-icon
              icon="BarChartIcon"
              size="21"
              class="text-info"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ maxWeight }}
          </h2>
          <p class="card-text">
            Poids maximum
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Advanced filters -->
    <b-card
      class="mb-2"
      aria-label="Filtres avancés pour la recherche de poids volumétriques"
    >
      <b-card-title>Filtres avancés</b-card-title>
      <span class="sr-only">Utilisez ces filtres pour affiner la liste des poids volumétriques selon vos critères logistiques.</span>
      <b-row>
        <!-- Basic filters -->
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group
            label="Nom du poids"
            aria-label="Filtrer par nom du poids volumétrique"
            description="Saisissez un nom pour filtrer les poids volumétriques."
          >
            <b-form-input
              v-model="filters.name"
              v-debounce:500ms="getWeights"
              placeholder="Rechercher par nom"
              class="filter-input"
              aria-label="Entrée du nom du poids volumétrique à filtrer"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group
            label="État"
            aria-label="Filtrer par état du poids volumétrique"
            description="Filtrez selon l'état actif ou inactif."
          >
            <v-select
              v-model="filters.active"
              :options="statusOptions"
              placeholder="Tous"
              :clearable="true"
              label="label"
              class="select-search-option"
              :reduce="option => option.value"
              aria-label="Liste des états disponibles pour filtrer les poids volumétriques"
              @input="getWeights"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Additional filters (toggleable) -->
      <div v-if="showMoreFilters">
        <b-row>
          <!-- Range filters -->
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label="Plage de poids minimum"
              aria-label="Filtrer par plage de poids minimum"
              description="Définissez une plage de poids minimum pour filtrer."
            >
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.minWeight"
                  v-debounce:500ms="getWeights"
                  placeholder="Min"
                  type="number"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.maxWeight"
                  v-debounce:500ms="getWeights"
                  placeholder="Max"
                  type="number"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>

          <!-- Date filters -->
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label="Date de création"
              aria-label="Filtrer par date de création"
              description="Filtrez les poids volumétriques selon leur date de création."
            >
              <b-row>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdAfter"
                    placeholder="À partir de"
                    locale="fr"
                    class="mb-2 filter-input"
                    @input="getWeights"
                  />
                </b-col>
                <b-col>
                  <b-form-datepicker
                    v-model="filters.createdBefore"
                    placeholder="Jusqu'à"
                    locale="fr"
                    class="filter-input"
                    @input="getWeights"
                  />
                </b-col>
              </b-row>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div
        class="d-flex justify-content-between mt-1"
        aria-label="Actions sur les filtres"
      >
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="!hasActiveFilters"
            @click="resetFilters"
          >
            <span>Réinitialiser</span>
            <span class="sr-only">Réinitialiser tous les filtres de recherche</span>
          </b-button>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          aria-label="Afficher ou masquer les filtres avancés"
          @click="toggleMoreFilters"
        >
          <span>{{ showMoreFilters ? 'Moins de filtres' : 'Plus de filtres' }}</span>
        </b-button>
      </div>
    </b-card>

    <!-- Header section with title and badge -->
    <div
      class="custom-search d-flex justify-content-between mb-2"
      aria-label="En-tête de la liste des poids volumétriques"
    >
      <div class="d-flex align-items-center">
        <h3
          class="mb-0 mr-2"
          aria-label="Titre de la liste des poids volumétriques"
        >
          Liste des Poids Volumétriques
        </h3>
        <b-badge
          variant="danger"
          pill
          class="badge-round"
          aria-label="Nombre total de poids volumétriques"
        >
          {{ totalWeights || 0 }}
        </b-badge>
      </div>
      <div class="d-flex">
        <b-form-group
          v-if="user.role==='admin'"
          class="mb-0"
        >
          <b-button
            :to="{ name: 'weight-add' }"
            variant="danger"
            class="d-inline-block"
            aria-label="Ajouter un nouveau poids volumétrique"
          >
            <feather-icon
              icon="PlusIcon"
              aria-hidden="true"
            />
            <span class="ml-1">Ajouter un poids</span>
          </b-button>
        </b-form-group>
      </div>
    </div>

    <!-- Loading states with skeletons -->
    <div
      v-if="isLoading"
      class="mb-2"
    >
      <b-card>
        <b-skeleton-table
          :rows="5"
          :columns="6"
          :table-props="{ bordered: true, striped: true }"
        />
      </b-card>
    </div>

    <!-- Enhanced table -->
    <vue-good-table
      v-else
      ref="weightsTable"
      aria-label="Tableau des poids volumétriques"
      :columns="columns"
      :rows="weights"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :pagination-options="{
        enabled: true,
        perPage: pageLength,
        mode: 'records'
      }"
      :total-rows="totalWeights"
      :is-loading="isLoading"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'weight', type: 'asc'}
      }"
      style-class="vgt-table bordered striped hover responsive"
      table-class="weights-table"
      :select-options="{
        enabled: user.role === 'admin',
        selectOnCheckboxOnly: true
      }"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
      @on-selected-rows-change="onSelectedRowsChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Enhanced weight name column -->
        <span v-if="props.column.field === 'weight'">
          <div class="d-flex align-items-center">
            <span
              class="font-weight-bold"
              :aria-label="'Nom du poids volumétrique : ' + props.row.weight"
            >{{ props.row.weight }}</span>
            <b-badge
              variant="light-info"
              class="ml-1"
              :aria-label="'Plage : ' + (props.row.min !== null ? props.row.min : '-') + ' à ' + (props.row.max !== null ? props.row.max : '-') + ' ' + (props.row.unit || 'kg')"
            >
              {{ `${props.row.min !== null ? props.row.min : '-'}-${props.row.max !== null ? props.row.max : '-'} ${props.row.unit || 'kg'}` }}
            </b-badge>
          </div>
        </span>

        <!-- Enhanced status column with clear action buttons -->
        <span v-else-if="props.column.field === 'is_active'">
          <div class="d-flex align-items-center justify-content-center">
            <b-badge
              :variant="props.row.is_active ? 'success' : 'danger'"
              class="badge-glow mr-2"
              pill
              :aria-label="props.row.is_active ? 'Poids volumétrique actif' : 'Poids volumétrique inactif'"
            >
              <feather-icon
                :icon="props.row.is_active ? 'CheckIcon' : 'XIcon'"
                size="12"
                class="mr-1"
                aria-hidden="true"
              />
              {{ props.row.is_active ? 'Actif' : 'Inactif' }}
            </b-badge>
          </div>
        </span>

        <!-- Enhanced action column with enable/disable and delete -->
        <span v-else-if="props.column.field === 'action'">
          <div class="d-flex align-items-center justify-content-center">
            <!-- View Details Button -->
            <b-button
              v-b-tooltip.hover
              variant="info"
              size="sm"
              class="btn-icon mr-1"
              title="Voir détails"
              :to="{ name: 'weight-show', params: { id: props.row.id }}"
              :aria-label="`Voir les détails du poids volumétrique ${props.row.weight}`"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
                aria-hidden="true"
              />
            </b-button>

            <!-- Edit Button -->
            <b-button
              v-if="user.role === 'admin'"
              v-b-tooltip.hover
              variant="warning"
              size="sm"
              class="btn-icon mr-1"
              title="Modifier"
              :to="{ name: 'weight-edit', params: { id: props.row.id }}"
              :aria-label="`Modifier le poids volumétrique ${props.row.weight}`"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                aria-hidden="true"
              />
            </b-button>

            <!-- Enable/Disable Toggle Button -->
            <b-button
              v-if="user.role === 'admin'"
              v-b-tooltip.hover
              :variant="props.row.is_active ? 'outline-warning' : 'outline-success'"
              size="sm"
              class="btn-icon mr-1"
              :title="props.row.is_active ? 'Désactiver' : 'Activer'"
              :disabled="toggleStatusLoading === props.row.id"
              :aria-label="props.row.is_active ? 'Désactiver le poids volumétrique ' + props.row.weight : 'Activer le poids volumétrique ' + props.row.weight"
              @click="confirmToggleStatus(props.row.id, !props.row.is_active)"
            >
              <b-spinner
                v-if="toggleStatusLoading === props.row.id"
                small
                type="grow"
              />
              <feather-icon
                v-else
                :icon="props.row.is_active ? 'EyeOffIcon' : 'EyeIcon'"
                size="14"
                aria-hidden="true"
              />
            </b-button>

            <!-- Delete Button (with usage check) -->
            <b-button
              v-if="user.role === 'admin'"
              v-b-tooltip.hover
              variant="danger"
              size="sm"
              class="btn-icon"
              title="Supprimer définitivement"
              :disabled="props.row.is_used"
              :aria-label="`Supprimer définitivement le poids volumétrique ${props.row.weight}`"
              @click="checkWeightUsageAndDelete(props.row.id)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
                aria-hidden="true"
              />
            </b-button>
          </div>
        </span>

        <!-- Column: Common -->
        <span v-else-if="props.column.field === 'min'">
          {{ props.row.min !== null ? props.row.min : '-' }}
        </span>
        <span v-else-if="props.column.field === 'max'">
          {{ props.row.max !== null ? props.row.max : '-' }}
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Empty state template -->
      <template slot="emptystate">
        <div
          class="empty-state text-center py-5"
          aria-label="Aucun poids volumétrique trouvé"
        >
          <feather-icon
            icon="PackageIcon"
            size="48"
            class="text-muted mb-2"
            aria-hidden="true"
          />
          <h4>Aucun poids volumétrique trouvé</h4>
          <p class="text-muted">
            {{ searchTerm ? 'Aucun poids correspondant à votre recherche.' : 'Aucun poids disponible.' }}
          </p>
          <div v-if="hasActiveFilters">
            <p>Essayez de modifier vos filtres</p>
            <b-button
              variant="outline-primary"
              size="sm"
              aria-label="Réinitialiser les filtres de recherche des poids volumétriques"
              @click="resetFilters"
            >
              Réinitialiser les filtres
            </b-button>
          </div>
        </div>
      </template>

      <!-- Add bulk actions for selected rows -->
      <template slot="selected-row-actions">
        <div
          class="selected-actions p-2 bg-light-primary rounded d-flex align-items-center"
          aria-label="Actions groupées sur les poids sélectionnés"
        >
          <span class="mr-2">{{ selectedRows.length }} sélectionné(s)</span>
          <b-button
            v-if="user.role === 'admin' && selectedRows.length > 0"
            variant="outline-success"
            size="sm"
            class="mr-1"
            aria-label="Activer tous les poids sélectionnés"
            @click="bulkActivate"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="14"
              class="mr-1"
              aria-hidden="true"
            />
            Activer
          </b-button>
          <b-button
            v-if="user.role === 'admin' && selectedRows.length > 0"
            variant="outline-danger"
            size="sm"
            aria-label="Désactiver tous les poids sélectionnés"
            @click="bulkDeactivate"
          >
            <feather-icon
              icon="XCircleIcon"
              size="14"
              class="mr-1"
              aria-hidden="true"
            />
            Désactiver
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="ml-2"
            aria-label="Annuler la sélection des poids"
            @click="clearSelection"
          >
            Annuler
          </b-button>
        </div>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap d-none d-sm-inline">
              Affichage
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> de {{ props.total }} résultat{{ props.total > 1 ? 's' : '' }} trouvé{{ props.total > 1 ? 's' : '' }} </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Status confirmation modal -->
    <b-modal
      id="confirm-status-modal"
      ref="confirmStatusModal"
      :title="statusModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
      @cancel="cancelStatusChange"
    >
      <p>{{ statusModalMessage }}</p>
    </b-modal>

    <!-- New bulk action confirmation modal -->
    <b-modal
      id="confirm-bulk-modal"
      ref="confirmBulkModal"
      :title="bulkModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleBulkConfirmed"
      @cancel="cancelBulkChange"
    >
      <p>{{ bulkModalMessage }}</p>
      <ul v-if="selectedRows.length <= 5">
        <li
          v-for="row in selectedRows"
          :key="row.id"
        >
          {{ row.weight }}
        </li>
      </ul>
      <p v-else>
        {{ selectedRows.length }} poids seront affectés.
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
  BCard,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BFormDatepicker,
  BSkeletonTable,
  BModal,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BFormDatepicker,
    BSkeletonTable,
    BModal,
    vSelect,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 100,
      user: {},
      columns: [
        {
          label: 'Poids',
          field: 'weight',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher poids',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
        },
        {
          label: 'Minimum',
          field: 'min',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Maximum',
          field: 'max',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Unité',
          field: 'unit',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'État',
          field: 'is_active',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center min-width-150',
          tdClass: 'text-center min-width-150',
          sortable: true,
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center min-width-150',
          tdClass: 'text-center min-width-150',
        },
      ],
      weights: [],
      searchTerm: '',
      showMoreFilters: false,
      totalWeights: 0,
      currentPage: 1,
      toggleStatusLoading: null,
      weightToToggle: null,
      newStatus: null,
      statusModalTitle: '',
      statusModalMessage: '',
      selectedRows: [],
      bulkModalTitle: '',
      bulkModalMessage: '',
      bulkAction: null,

      // Advanced filters
      filters: {
        name: '',
        active: null,
        minWeight: null,
        maxWeight: null,
        createdAfter: null,
        createdBefore: null,
      },

      // Filter options
      statusOptions: [
        { value: true, label: 'Actif' },
        { value: false, label: 'Inactif' },
      ],
    }
  },
  computed: {
    activeCount() {
      return this.weights.filter(w => w.is_active).length
    },
    minWeight() {
      const minValues = this.weights
        .filter(w => w.min !== null)
        .map(w => parseFloat(w.min))

      return minValues.length > 0 ? Math.min(...minValues) : 'N/A'
    },
    maxWeight() {
      const maxValues = this.weights
        .filter(w => w.max !== null)
        .map(w => parseFloat(w.max))

      return maxValues.length > 0 ? Math.max(...maxValues) : 'N/A'
    },
    hasActiveFilters() {
      return (
        this.filters.name !== ''
        || this.filters.active !== null
        || this.filters.minWeight !== null
        || this.filters.maxWeight !== null
        || this.filters.createdAfter !== null
        || this.filters.createdBefore !== null
      )
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getWeights()
  },
  methods: {
    // Toggle more filters
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },

    // Get weights with applied filters
    async getWeights() {
      this.isLoading = true
      try {
        let url = '/api/weights/'

        // Add query params for filters
        const params = new URLSearchParams()
        if (this.filters.name) params.append('weight__icontains', this.filters.name)
        if (this.filters.active !== null) params.append('is_active', this.filters.active)
        if (this.filters.minWeight) params.append('min__gte', this.filters.minWeight)
        if (this.filters.maxWeight) params.append('max__lte', this.filters.maxWeight)

        // Add date filters with proper formatting
        if (this.filters.createdAfter) {
          params.append('created_at__gte', this.formatDate(this.filters.createdAfter))
        }
        if (this.filters.createdBefore) {
          params.append('created_at__lte', this.formatDate(this.filters.createdBefore))
        }

        // Append params to URL if any exist
        if (params.toString()) url = `${url}?${params.toString()}`

        const { data } = await axios.get(url)
        // Handle paginated response from backend if implemented
        if (data.results) {
          this.weights = data.results
          this.totalWeights = data.count
        } else {
          this.weights = data
          this.totalWeights = data.length
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des poids')
      } finally {
        this.isLoading = false
      }
    },

    // Format date for API requests
    formatDate(date) {
      if (!date) return null

      // Convert to YYYY-MM-DD format
      if (typeof date === 'string' && date.includes('-')) {
        return date // Already in correct format
      }

      const d = new Date(date)
      let month = `${d.getMonth() + 1}`
      let day = `${d.getDate()}`
      const year = d.getFullYear()

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`

      return [year, month, day].join('-')
    },

    // Reset filters
    resetFilters() {
      this.filters = {
        name: '',
        active: null,
        minWeight: null,
        maxWeight: null,
        createdAfter: null,
        createdBefore: null,
      }
      this.getWeights()
    },

    // Check if weight is used before deletion
    async checkWeightUsageAndDelete(id) {
      try {
        // First check if weight is used by suppliers
        const { data } = await axios.get(`/api/weights/usage-check/${id}/`)

        if (data.is_used) {
          this.$swal({
            title: 'Suppression impossible',
            html: `
              <p>Ce poids ne peut pas être supprimé car il est utilisé par:</p>
              <ul style="text-align: left; margin: 1rem 0;">
                ${data.usage_details.suppliers ? `<li><strong>${data.usage_details.suppliers}</strong> fournisseur(s)</li>` : ''}
                ${data.usage_details.orders ? `<li><strong>${data.usage_details.orders}</strong> commande(s)</li>` : ''}
              </ul>
              <p class="text-muted">Vous pouvez le désactiver à la place.</p>
            `,
            icon: 'error',
            confirmButtonText: 'Compris',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        } else {
          // Safe to delete
          this.confirmPermanentDelete(id)
        }
      } catch (error) {
        // If check fails, show warning and allow user to proceed with caution
        this.confirmPermanentDelete(id, true)
      }
    },

    // Confirm permanent deletion
    async confirmPermanentDelete(id, skipUsageCheck = false) {
      const warningText = skipUsageCheck
        ? 'Impossible de vérifier l\'utilisation. Êtes-vous sûr de vouloir supprimer ce poids?'
        : 'Cette action supprimera définitivement ce poids et ne peut pas être annulée!'

      try {
        const result = await this.$swal({
          title: 'Supprimer définitivement?',
          text: warningText,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, supprimer!',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          await this.deleteWeight(id)
        }
      } catch (e) {
        // No action needed on modal dismiss
      }
    },

    // Delete a weight
    async deleteWeight(id) {
      try {
        this.isLoading = true
        await axios.delete(`/api/weights/delete/${id}/`)
        await this.getWeights() // Refresh data
        this.showToast('success', 'top-center', 'Poids supprimé avec succès')
      } catch (error) {
        if (error.response?.status === 409) {
          this.showToast('danger', 'top-center', 'Ce poids est utilisé et ne peut pas être supprimé')
        } else {
          this.showToast('danger', 'top-center', 'Erreur lors de la suppression')
        }
      } finally {
        this.isLoading = false
      }
    },

    // Status toggle methods
    confirmToggleStatus(id, makeActive) {
      this.weightToToggle = id
      this.newStatus = makeActive
      this.statusModalTitle = makeActive ? 'Activer le poids' : 'Désactiver le poids'
      this.statusModalMessage = makeActive
        ? 'Êtes-vous sûr de vouloir activer ce poids?'
        : 'Êtes-vous sûr de vouloir désactiver ce poids?'
      this.$refs.confirmStatusModal.show()
    },

    cancelStatusChange() {
      this.weightToToggle = null
      this.newStatus = null
    },

    async handleStatusConfirmed() {
      if (this.weightToToggle !== null) {
        await this.toggleStatus(this.weightToToggle, this.newStatus)
      }
    },

    async toggleStatus(id, makeActive) {
      try {
        this.toggleStatusLoading = id
        await axios.post(`/api/weights/toggle-status/${id}/`, { is_active: makeActive })

        // Update the item in the weights array to avoid a full reload
        const index = this.weights.findIndex(weight => weight.id === id)
        if (index !== -1) {
          this.weights[index].is_active = makeActive
        }

        this.showToast(
          'success',
          'top-center',
          `Le poids a été ${makeActive ? 'activé' : 'désactivé'} avec succès`,
        )
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      } finally {
        this.toggleStatusLoading = null
        this.weightToToggle = null
        this.newStatus = null
      }
    },

    // Bulk actions
    onSelectedRowsChange({ selectedRows }) {
      this.selectedRows = selectedRows
    },

    clearSelection() {
      this.$refs.weightsTable.unselectAllRows()
      this.selectedRows = []
    },

    bulkActivate() {
      this.bulkModalTitle = 'Activer les poids sélectionnés'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir activer tous les poids sélectionnés?'
      this.bulkAction = 'activate'
      this.$refs.confirmBulkModal.show()
    },

    bulkDeactivate() {
      this.bulkModalTitle = 'Désactiver les poids sélectionnés'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir désactiver tous les poids sélectionnés?'
      this.bulkAction = 'deactivate'
      this.$refs.confirmBulkModal.show()
    },

    cancelBulkChange() {
      this.bulkAction = null
    },

    async handleBulkConfirmed() {
      if (!this.bulkAction) return

      const isActivate = this.bulkAction === 'activate'
      const ids = this.selectedRows.map(({ id }) => id)

      try {
        this.isLoading = true
        await axios.post('/api/weights/bulk-toggle-status/', {
          ids,
          is_active: isActivate,
        })

        // Update the status in the local data
        ids.forEach(id => {
          const index = this.weights.findIndex(weight => weight.id === id)
          if (index !== -1) {
            this.weights[index].is_active = isActivate
          }
        })

        this.clearSelection()
        this.showToast(
          'success',
          'top-center',
          `${ids.length} poids ont été ${isActivate ? 'activés' : 'désactivés'} avec succès`,
        )
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors de la mise à jour des statuts')
      } finally {
        this.isLoading = false
        this.bulkAction = null
      }
    },

    // Pagination methods
    onPageChange({ currentPage }) {
      this.currentPage = currentPage
      this.getWeights()
    },

    onPerPageChange({ currentPerPage }) {
      this.pageLength = currentPerPage
      this.currentPage = 1
      this.getWeights()
    },

    // Toast notification
    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import "@core/scss/vue/libs/vue-select.scss";

// Enhanced table styles
.weights-table {
  .vgt-table {
    border-collapse: separate;
    border-spacing: 0;

    th {
      position: relative;
      font-weight: 600;

      &.active-sorting {
        background-color: rgba($color: #7367f0, $alpha: 0.1);
        color: #7367f0;
      }

      &.sorting-header {
        cursor: pointer;

        &:hover {
          background-color: rgba($color: #7367f0, $alpha: 0.05);
        }
      }
    }

    td {
      vertical-align: middle;
    }
  }

  // Status badges
  .badge-glow {
    &.bg-success {
      box-shadow: 0 0 10px rgba(40, 199, 111, 0.6);
    }

    &.bg-danger {
      box-shadow: 0 0 10px rgba(234, 84, 85, 0.6);
    }
  }
}

// Responsive table styles
.vgt-table {
  min-width: 800px;

  @media (max-width: 992px) {
    .min-width-200 {
      min-width: 150px;
    }

    .min-width-150 {
      min-width: 120px;
    }

    .min-width-100 {
      min-width: 80px;
    }
  }
}

// Improve button styling in action column
.btn-icon {
  padding: 0.25rem 0.5rem;

  @media (max-width: 576px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.8rem;
  }
}

// Consistent form field sizing
.form-control, .vs__dropdown-toggle, .filter-input {
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #ea5455;
    box-shadow: none;
  }
}

// Improve v-select styling to match regular inputs
.select-search-option {
  .vs__dropdown-toggle {
    padding: 5px 0;
    min-height: 40px;
    display: flex;
    align-items: center;
  }

  .vs__selected-options {
    padding: 0 6px;
    align-items: center;
  }

  .vs__search {
    padding: 5px 8px;
    margin: 0;
  }

  .vs__actions {
    padding-top: 0;
    padding-bottom: 0;
  }

  .vs__clear {
    height: 22px;
  }

  .vs__open-indicator {
    height: 17px;
  }
}

// Badge styling to match the image
.badge-round {
  padding: 0.3rem 0.65rem;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: #ea5455 !important;
}

// Match button styling
.btn-danger {
  background-color: #ea5455 !important;
  border-color: #ea5455 !important;
}

// Add styles for statistic cards
.card {
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #5e5873;
  }

  .text-center {
    min-height: 120px;
  }

  h2 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  &.bg-light-primary {
    background-color: rgba($color: #7367f0, $alpha: 0.12) !important;
  }

  &.bg-light-success {
    background-color: rgba($color: #28c76f, $alpha: 0.12) !important;
  }

  &.bg-light-warning {
    background-color: rgba($color: #ff9f43, $alpha: 0.12) !important;
  }

  &.bg-light-info {
    background-color: rgba($color: #00cfe8, $alpha: 0.12) !important;
  }
}

// Empty state styling
.empty-state {
  padding: 3rem 1rem;

  svg {
    opacity: 0.6;
  }
}

// Animations
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
